/* Los bloques del día tal como los usa la ventanilla: su tramo en minutos, su monumento y su terreno. */
import type { Bloque, Dia } from '../datos/tipos.ts';
import type { Hito, Terreno } from './dibujo.ts';

export interface Tramo { ini: number; fin: number; iniTxt: string; finTxt: string }
export interface BloqueV extends Bloque { i: number; t: Tramo; mid: number; hito: Hito | null; terreno: Terreno }

const aMin = (t: string) => { const m = /(\d{1,2}):(\d{2})/.exec(t || ''); return m ? +m[1] * 60 + +m[2] : null; };

/** Tramo de un bloque. Las horas vienen ya en minutos del importador; el texto, de la hora escrita. */
export function tramo(b: Bloque): Tramo {
  const [a, z = ''] = b.hora.replace(/~/g, '').split('–');
  const ini = b.ini ?? aMin(a) ?? 0;
  let fin = /late/.test(z) ? 26 * 60 : b.fin ?? aMin(z);
  if (fin == null) fin = ini + 30;
  if (fin <= ini) fin += 1440;
  return { ini, fin, iniTxt: a.trim().replace(' +1', ''), finTxt: /late/.test(z) ? 'tarde' : z.replace('+1', '').trim() };
}

/* Qué se ve en cada parada (el orden importa: gana la primera regla que encaja) */
const REGLAS_HITO: [RegExp, Hito][] = [
  [/^Examen ·/, 'examen'],
  [/Nintendo/i, 'nintendo'], [/USJ/, 'usj'], [/ENNE RING|anillo/i, 'anillo'], [/^Volver a Kyoto Station|^Kyoto Station$/, 'estacion'],
  [/Noche abierta/i, 'noche'], [/Tsutenkaku|Shinsekai/i, 'tsutenkaku'], [/Minoh/i, 'cascada'],
  [/Skytree|Solamachi|Sumida/i, 'skytree'], [/Tokyo Tower|Zōjō/i, 'tokyotower'], [/Nijō|Nijo/i, 'nijo'], [/Castle/i, 'castillo'],
  [/Kaiyukan|Tempozan/i, 'noria'], [/Nara Park|ciervos/i, 'ciervo'], [/Fushimi/i, 'toriis'], [/Katsuo/i, 'daruma'], [/Meiji/i, 'meiji'], [/Kasuga/i, 'torii'],
  [/Kinkaku/i, 'kinkaku'], [/Kiyomizu/i, 'kiyomizu'], [/Tōdai|Todai/i, 'todaiji'], [/Asakusa|Sensō|Kaminarimon/i, 'kaminarimon'],
  [/Nigatsu/i, 'templo'], [/Gion|Higashiyama|Pontocho|Ueno|Yasaka|kimono/i, 'pagoda'],
  [/Gyoen/i, 'jardin'], [/Arashiyama|Sagano|Torokko|Kameoka/i, 'bambu'], [/teamLab/i, 'orbes'],
  [/Burgos|Madrid → Burgos/, 'carretera'], [/kart/i, 'kart'],
  [/JDM|Wangan|Daikoku|parkings|Haruna → Toyosu|Narita → Haruna|Hakone → Narita|→ Hakone/i, 'coche'],
  [/Haruna/i, 'haruna'], [/\bAshi\b|Moto-Hakone|Heiwa/i, 'ashi'], [/Hakone|Turnpike|Daikanzan/i, 'fuji'], [/Nozomi|Shinkansen/i, 'tren'],
  [/TK\d|NH\d|FRA → MAD|Haneda|KIX|ITM|Conexión|Aena|HND/i, 'avion'], [/onsen|Shoei/i, 'onsen'],
  [/Yamato|consigna|Bounce|Crosta|cabinas|Repack|Maletas/i, 'maleta'],
  [/Dotonbori|Glico/i, 'dotonbori'], [/^Shibuya(?! PARCO)|Shibuya Sky|Scramble Square|Traslado a Shibuya/i, 'shibuya'],
  [/Den Den/i, 'denden'], [/Akihabara|Electric Town/i, 'akiba'], [/Nakano/i, 'nakano'], [/Ikebukuro|Sunshine|Animate/i, 'ikebukuro'], [/Ameyoko/i, 'ameyoko'],
  [/Kappabashi/i, 'kappabashi'], [/Tokyo Station$|Marunouchi/i, 'tokyoeki'], [/Shinjuku(?! Gyoen)/i, 'shinjuku'],
  [/Shinjuku|Shibuya|Akihabara|Den Den|Ikebukuro|Nakano|ROUND1|Namba|Shinsaibashi|Harajuku|Ameyoko|Kuromon|PARCO|Umeda|BragMen|Kappabashi|Marunouchi|Character|compras|Tombori|Festival|Minato/i, 'neon'],
  [/cena|comida|desayuno/i, 'farol'], [/→/, 'metro'],
  [/check|Checkout|Regreso|Hotel|Turtle|AARON|iru|Casa|Despertar|Descanso|Llegada/i, 'casa'],
];
/* En un traslado «A → B» se ve lo de B: «Kinkaku-ji → Notre Dame» ya no es el pabellón dorado. Si B no
   tiene nada propio, del texto entero solo vale el medio de transporte (coche, tren, avión…). */
const DE_CAMINO = new Set<Hito>(['coche', 'carretera', 'tren', 'metro', 'avion', 'maleta', 'casa', 'farol']);
export const hitoDe = (b: Bloque): Hito | null => {
  if (!b.que.includes('→')) return REGLAS_HITO.find(([re]) => re.test(b.que))?.[1] ?? null;
  const destino = b.que.split('→').pop()!;
  return REGLAS_HITO.find(([re]) => re.test(destino))?.[1]
    ?? REGLAS_HITO.find(([re, h]) => DE_CAMINO.has(h) && re.test(b.que))?.[1] ?? null;
};

function terrenoDe(b: BloqueV): Terreno {
  switch (b.hito) {
    case 'avion': return 'nubes';
    case 'fuji': case 'bambu': case 'ciervo': case 'cascada': case 'haruna': case 'daruma': return 'montana';
    case 'ashi': return 'mar';
    case 'noria': return 'mar';
    case 'coche': return /Wangan|Daikoku|Rainbow/.test(b.que) ? 'mar' : /Haruna|Hakone|Narita|Turnpike/.test(b.que) ? 'montana' : 'ciudad';
    case 'carretera': return 'montana';
    default: return 'ciudad';
  }
}

const cache = new WeakMap<Dia, BloqueV[]>();
export function bloquesDe(dia: Dia): BloqueV[] {
  let bl = cache.get(dia);
  if (!bl) {
    bl = dia.bloques.map((b, i) => {
      const t = tramo(b);
      const x = { ...b, i, t, mid: (t.ini + Math.min(t.fin, t.ini + 240)) / 2, hito: hitoDe(b), terreno: 'ciudad' } as BloqueV;
      x.terreno = terrenoDe(x);
      return x;
    });
    cache.set(dia, bl);
  }
  return bl;
}

/** El bloque que representa el día (su monumento sale en el vagón y se abre por él). */
const MALOS: (Hito | null)[] = ['casa', 'farol', 'tren', 'metro', 'maleta', 'examen', 'estacion'];
export function claveDe(dia: Dia): BloqueV {
  const bl = bloquesDe(dia);
  const fijado = dia.clave ? bl.find(b => b.id === dia.clave) : undefined;   // el autor lo fija en la config del viaje
  return fijado ?? bl.reduce<[BloqueV, number]>((m, b) => { const p = puntosClave(b); return p > m[1] ? [b, p] : m; }, [bl[0], -Infinity])[0];
}

/* Qué hace a un bloque el momento del día: que se proteja (A), que tenga un monumento de verdad, que
   sea a hora fija y que dure. Lo de paso (desayunos, salidas, facturar, prepararse) casi nunca lo es. */
const DE_PASO = /desayuno|comida|cena|salida|preparaci|facturaci|seguridad|check|regreso|volver|descanso|recoger|margen/i;
const PESO_HITO: Partial<Record<Hito, number>> = { neon: 1, coche: 1, avion: 0.5, onsen: 1.5 };
function puntosClave(b: BloqueV): number {
  let p = { A: 3, B: 1.5, C: 0 }[b.pri] ?? 0;
  if (b.hito && !MALOS.includes(b.hito)) p += PESO_HITO[b.hito] ?? 2.5;
  else if (b.hito) p -= 1.5;
  if (/FIJO|CONFIRMADO/.test(b.modo)) p += 0.5;
  if (b.tipo === 'traslado' || b.tipo === 'comida' || b.tipo === 'alojamiento') p -= 2;
  if (DE_PASO.test(b.que)) p -= 3;
  return p + Math.min(5, (Math.min(b.t.fin, b.t.ini + 300) - b.t.ini) / 60) * 0.25;
}

/** El bloque que toca a una hora. Si hay varios a la vez (un examen dentro de un tramo en coche), el que
   empezó más tarde, que es el más concreto; los saltados no cuentan. Si no hay ninguno, el siguiente. */
export function bloqueEn(bl: BloqueV[], p: number, saltado: (b: BloqueV) => boolean): BloqueV {
  let actual: BloqueV | undefined;
  for (const b of bl) if (b.t.ini <= p && p < Math.min(b.t.fin, b.t.ini + 300) && !saltado(b) && (!actual || b.t.ini >= actual.t.ini)) actual = b;
  return actual ?? bl.find(b => b.t.ini > p) ?? bl.at(-1)!;
}
